import { useState } from 'react';

import { Button, Icon, Stack, Text } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { ToolInvocation } from './ToolCallStatus';

interface CustomInstructionSuggestionCardProps {
  toolInvocation: ToolInvocation;
  onConfirm: (message: string) => void;
  onDismiss: (message: string) => void;
}

interface InstructionSuggestion {
  instruction: string;
  reason: string;
}

function parseSuggestion(
  toolInvocation: ToolInvocation,
): InstructionSuggestion | null {
  const data =
    toolInvocation.state === 'output-available'
      ? toolInvocation.output
      : toolInvocation.input;

  if (!data || typeof data !== 'object') return null;

  const { instruction, reason } = data as Record<string, unknown>;
  if (typeof instruction !== 'string' || typeof reason !== 'string') {
    return null;
  }

  return { instruction, reason };
}

export const CustomInstructionSuggestionCard = ({
  toolInvocation,
  onConfirm,
  onDismiss,
}: CustomInstructionSuggestionCardProps) => {
  const { t } = useTranslation();
  const [acted, setActed] = useState(false);
  const isComplete = toolInvocation.state === 'output-available';

  if (!isComplete) {
    return (
      <Stack
        className="w-fit rounded-full border border-border-insights-subtle bg-bg-insights-subtle px-3 py-1.5"
        inline
        align="center"
        gap={2}
        fullWidth={false}
      >
        <Icon
          name="WandSparkles"
          className="animate-pulse text-text-insights"
        />
        <Text size="xs">{t('chat.instructions.preparingSuggestion')}</Text>
      </Stack>
    );
  }

  const suggestion = parseSuggestion(toolInvocation);

  if (!suggestion) {
    return null;
  }

  return (
    <Stack
      gap={3}
      className="rounded-lg border border-border-primary bg-bg-secondary p-4"
    >
      <Stack gap={2} align="center" direction="horizontal">
        <Icon name="Settings" size="lg" className="text-text-tertiary" />
        <Text inline size="xs" weight="semibold" color="secondary">
          {t('chat.instructions.suggestedTitle')}
        </Text>
      </Stack>

      <Stack
        gap={1}
        className="rounded-md border border-border-secondary bg-bg-primary p-3"
      >
        <Text size="xs" weight="semibold">
          {suggestion.instruction}
        </Text>
        <Text size="xs" color="tertiary" className="mt-1 italic">
          {suggestion.reason}
        </Text>
      </Stack>

      <Stack gap={2} direction="horizontal" justify="end">
        <Button
          variant="outline"
          size="sm"
          disabled={acted}
          onClick={() => {
            setActed(true);
            onDismiss?.(t('chat.instructions.dismissMessage'));
          }}
        >
          {t('chat.instructions.dismiss')}
        </Button>
        <Button
          size="sm"
          disabled={acted}
          onClick={() => {
            setActed(true);
            onConfirm?.(t('chat.instructions.confirmMessage'));
          }}
        >
          <Icon name="Save" size="auto" />
          {t('chat.instructions.save')}
        </Button>
      </Stack>
    </Stack>
  );
};
